import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Button, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import Dayjs from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Notifications } from '../../store/slices';
import { getReportModerator } from '../../api';
import { AppDialog } from '../ui';
import { ORDER_STATUSES, orderStatus } from '../../lib/orderStatus';
import { MIN_DATE_SENTINEL, dateParam } from '../../lib/orderFilters';
import { GENERIC_ERROR_MESSAGE } from '../../lib/errorMessage';
import { useStyles } from './styles';

const DEFAULT_FILTERS = {
    sort: 'createdAt',
    sortDirection: 'DESC',
};

/** "Export CSV": asks order-api for a print-file report of the chosen statuses and dates, then downloads it. */
export default function OrdersReport(props: any) {
    const { isPopUp, popUpHandler } = props;
    const classes = useStyles();
    const [reportFilters, setReportFilters] = useState<any>({ ...DEFAULT_FILTERS });
    const dataLink = useRef<HTMLAnchorElement>(null);
    const [data, setData] = useState<string | undefined>();
    const [pending, setPending] = useState(false);
    const dispatch = useDispatch();
    const { id } = useParams();

    const handleFilterChange = (name: string, value: any) => {
        const next = { ...reportFilters, [name]: value };
        if (name === 'createdAt_max' && !next.createdAt_min) next.createdAt_min = MIN_DATE_SENTINEL;
        if (name === 'createdAt_min' && !next.createdAt_max) next.createdAt_max = Dayjs(new Date()).format('YYYY-MM-DD');
        setReportFilters(next);
    };

    const getReport = async () => {
        if (pending) return;
        setPending(true);
        try {
            await getReportModerator({
                companyId: id,
                queryParams: { ...reportFilters },
                onSuccess: (result) => {
                    setData(result.downloadUrl);
                    dispatch(Notifications.setNotifications({ text: 'Download started', type: 'success' }));
                    setReportFilters({ ...DEFAULT_FILTERS });
                    popUpHandler(true);
                },
            });
        } catch (error) {
            console.warn('[orders] report failed', error);
            dispatch(Notifications.setNotifications({ text: GENERIC_ERROR_MESSAGE, type: 'error' }));
        } finally {
            setPending(false);
        }
    };

    useEffect(() => {
        if (data) dataLink.current?.click();
    }, [data]);

    const statuses: number[] = reportFilters.orderStatusId ?? [];

    return (
        <>
            <a download href={data} ref={dataLink} rel="noreferrer nofollow" target="_blank" style={{ display: 'none' }} tabIndex={-1}>Download</a>
            <AppDialog
                open={Boolean(isPopUp)}
                onClose={() => popUpHandler(true)}
                title="Export orders CSV"
                description="A print-file report (CSV) of the orders that match the filters below. Leave the status empty to include all statuses."
                pending={pending}
                maxWidth="xs"
                actions={(
                    <>
                        <Button variant="outlined" onClick={() => popUpHandler(true)}>Cancel</Button>
                        <Button variant="contained" onClick={getReport} disabled={pending}>Download CSV</Button>
                    </>
                )}
            >
                <div className={classes.form}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="report-status-label">Status</InputLabel>
                        <Select
                            labelId="report-status-label"
                            label="Status"
                            name="orderStatusId"
                            multiple
                            value={statuses}
                            onChange={(event) => handleFilterChange('orderStatusId', event.target.value)}
                            renderValue={(selected) => selected.map((value) => orderStatus(value).label).join(', ')}
                        >
                            {ORDER_STATUSES.map((status) => (
                                <MenuItem key={status.id} value={status.id}>{status.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <div className={classes.dates}>
                            <DatePicker
                                label="From"
                                format="YYYY-MM-DD"
                                value={reportFilters.createdAt_min && reportFilters.createdAt_min !== MIN_DATE_SENTINEL ? Dayjs(reportFilters.createdAt_min) : null}
                                maxDate={reportFilters.createdAt_max ? Dayjs(reportFilters.createdAt_max) : Dayjs(new Date())}
                                onChange={(value) => { const iso = dateParam(value); if (iso) handleFilterChange('createdAt_min', iso); }}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                            <DatePicker
                                label="To"
                                format="YYYY-MM-DD"
                                value={reportFilters.createdAt_max ? Dayjs(reportFilters.createdAt_max) : Dayjs(new Date())}
                                maxDate={Dayjs(new Date())}
                                onChange={(value) => { const iso = dateParam(value); if (iso) handleFilterChange('createdAt_max', iso); }}
                                slotProps={{ textField: { size: 'small', fullWidth: true } }}
                            />
                        </div>
                    </LocalizationProvider>
                </div>
            </AppDialog>
        </>
    );
}
